/**
 * The subscription client's calls: "pay to keep going" (extras), the reports list the plan page reads, and changing a
 * plan (checkout confirm, change preview, change, cancel, resume, sync). Stripe stays in TEST mode server-side (a live
 * key is refused with 503 unless the founder opens it); nothing here ever sees a card, a key or a price id: the server
 * builds every Checkout Session, invoice preview and subscription change and returns only what the page prints.
 *
 * Nothing is charged until the creator presses the button that says its price, and nothing is granted until the
 * server has retrieved the PAID session with its own secret key (confirm is idempotent on the session id).
 */
import apiConfig from '../config/apiConfig';
import { parseUsageBlocks, type ExtraKind, type UsageBlocks } from '../components/billing/usage';
import type { SubscriptionInfo, SubscriptionTier } from '../contexts/SubscriptionContext';
import { reportRowOf, REPORTS_LIST_MAX, type ReportShelfRow } from './assistantService';

export class ExtrasError extends Error {
  /** 409 not_paid · 409 unexpected_amount · 404 not found · 503 not open / not set up · 502 Stripe down · other. */
  readonly code: 'not_paid' | 'unexpected_amount' | 'not_found' | 'unavailable' | 'stripe' | 'other';
  readonly status: number;
  constructor(message: string, code: ExtrasError['code'], status: number) {
    super(message);
    this.name = 'ExtrasError';
    this.code = code;
    this.status = status;
  }
}

async function fail(res: Response, fallback: string): Promise<never> {
  let detail: unknown = null;
  try { detail = (await res.json())?.detail; } catch { /* not JSON */ }
  const d = detail as { code?: unknown; message?: unknown } | string | null;
  const message = typeof d === 'string' ? d : typeof d?.message === 'string' ? d.message : fallback;
  const code: ExtrasError['code'] = res.status === 404 ? 'not_found'
    : res.status === 409 && typeof d === 'object' && d?.code === 'not_paid' ? 'not_paid'
      : res.status === 409 && typeof d === 'object' && d?.code === 'unexpected_amount' ? 'unexpected_amount'
        : res.status === 503 ? 'unavailable' : res.status === 502 ? 'stripe' : 'other';
  throw new ExtrasError(message, code, res.status);
}

/** POST /api/subscription/extras/checkout {kind} → the Stripe Checkout url (mode 'payment', one-off). */
export async function startExtraCheckout(kind: ExtraKind, token: string): Promise<{ url: string; session_id: string | null }> {
  const res = await fetch(`${apiConfig.baseUrl}/api/subscription/extras/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kind }),
  });
  if (!res.ok) await fail(res, 'Checkout couldn’t start. Nothing was charged; try again.');
  const data = await res.json();
  if (typeof data?.url !== 'string' || !/^https:\/\//.test(data.url)) {
    throw new ExtrasError('Checkout couldn’t start. Nothing was charged; try again.', 'other', 200);
  }
  return { url: data.url, session_id: typeof data.session_id === 'string' ? data.session_id : null };
}

export interface ExtraGrant {
  kind: ExtraKind;
  /** "about 50 questions" / "5 reports" — the server's words. */
  adds: string;
  /** Chat: the month it's for ("2026-09"). Reports: when the pack expires (ISO date). */
  month?: string;
  expires_on?: string;
  already_granted: boolean;
}

export interface ExtraConfirmation { extra: ExtraGrant; usage: UsageBlocks | null }

/** POST /api/subscription/extras/confirm {session_id}: grants ONCE (a second confirm returns the same grant). */
export async function confirmExtra(sessionId: string, token: string): Promise<ExtraConfirmation> {
  const res = await fetch(`${apiConfig.baseUrl}/api/subscription/extras/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ session_id: sessionId }),
  });
  if (!res.ok) await fail(res, 'We couldn’t check that payment. Nothing was added; try again.');
  const data = await res.json();
  const e = data?.extra ?? {};
  const kind: ExtraKind = e.kind === 'reports' ? 'reports' : 'chat';
  return {
    extra: { kind, adds: typeof e.adds === 'string' ? e.adds : '', already_granted: !!e.already_granted,
      ...(typeof e.month === 'string' ? { month: e.month } : {}), ...(typeof e.expires_on === 'string' ? { expires_on: e.expires_on } : {}) },
    usage: parseUsageBlocks(data),
  };
}

/** A filed report as the shelf lists it (GET /api/assistant/reports; never the document itself): the very row the
 *  Reports tab reads — a chat's report, or a Deal Room read that used one (source 'deal'). */
export type ReportRow = ReportShelfRow;

/** The creator's reports, newest first — the plan page's This month lists this month's. Owner-only, no-store server-side.
 *  Read through the shelf's own reportRowOf, so the plan page and the Reports tab list the same rows: the newest 100
 *  (the server's most; not paged). */
export async function listReportRows(token: string, limit = REPORTS_LIST_MAX): Promise<ReportRow[]> {
  const n = Math.max(1, Math.min(REPORTS_LIST_MAX, Math.round(limit)));
  const res = await fetch(`${apiConfig.baseUrl}/api/assistant/reports?limit=${n}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
  if (!res.ok) throw new Error('Could not read your reports.');
  const data = await res.json();
  const rows: unknown[] = Array.isArray(data?.reports) ? data.reports : [];
  return rows.map(reportRowOf).filter((r): r is ReportRow => !!r);
}

// ── changing a plan (the backend contract B2–B12) ────────────────────────────────────────────────

/** A refusal or failure from the plan endpoints, with the server's code and words (detail {code, message}). */
export class PlanChangeError extends Error {
  /** e.g. has_subscription · comped · checkout_pending · free_plan · not_self_serve · no_subscription · same_tier ·
   *  payment_due · payment_failed · preview_expired · not_complete · not_found · portal_not_ready ·
   *  no_billing_account · stripe_unavailable — or 'other' when the server sent none. */
  readonly code: string;
  readonly status: number;
  /** 402 payment_failed: Stripe's hosted invoice, where the creator can finish paying (null when there is none). */
  readonly invoiceUrl: string | null;
  /** Whether the message is the server's own words (safe to print as sent), not our fallback. */
  readonly fromServer: boolean;
  constructor(message: string, code: string, status: number, invoiceUrl: string | null = null, fromServer = false) {
    super(message);
    this.name = 'PlanChangeError';
    this.code = code;
    this.status = status;
    this.invoiceUrl = invoiceUrl;
    this.fromServer = fromServer;
  }
}

/** Turn a non-OK plan response into a PlanChangeError carrying the server's code and words. */
export async function planFailure(res: Response, fallback: string): Promise<never> {
  let detail: unknown = null;
  try { detail = (await res.json())?.detail; } catch { /* not JSON */ }
  const d = detail as { code?: unknown; message?: unknown; invoice_url?: unknown } | string | null;
  const fromServer = typeof d === 'string' ? !!d : typeof d?.message === 'string' && !!d.message;
  const message = typeof d === 'string' && d ? d : typeof d === 'object' && typeof d?.message === 'string' && d.message ? d.message : fallback;
  const code = typeof d === 'object' && typeof d?.code === 'string' && d.code ? d.code
    : res.status === 404 ? 'not_found' : res.status === 502 ? 'stripe_unavailable' : 'other';
  const invoice = typeof d === 'object' && typeof d?.invoice_url === 'string' && /^https:\/\//.test(d.invoice_url) ? d.invoice_url : null;
  throw new PlanChangeError(message, code, res.status, invoice, fromServer);
}

const post = (path: string, token: string, body?: unknown) => fetch(`${apiConfig.baseUrl}/api/subscription${path}`, {
  method: 'POST',
  headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), Authorization: `Bearer ${token}` },
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
});

/** A plan as the server sent it — only an object with a tier is a plan. */
function planInfo(data: unknown): SubscriptionInfo {
  const d = data as Partial<SubscriptionInfo> | null;
  if (!d || typeof d !== 'object' || typeof d.tier !== 'string') throw new PlanChangeError('Nothing changed. Try again.', 'other', 200);
  return d as SubscriptionInfo;
}

export interface CheckoutConfirmation {
  info: SubscriptionInfo;
  /** tier: the plan the checkout bought. settled: Stripe says it's paid AND the account is on that plan now. */
  checkout: { tier: SubscriptionTier | null; settled: boolean };
}

/** POST /checkout/confirm {session_id} — back from Stripe: the server retrieves the session and syncs the plan. */
export async function confirmPlanCheckout(sessionId: string, token: string): Promise<CheckoutConfirmation> {
  const res = await post('/checkout/confirm', token, { session_id: sessionId });
  if (!res.ok) await planFailure(res, 'Stripe isn’t answering. If you paid, check again in a minute.');
  const data = await res.json();
  const c = (data?.checkout ?? {}) as { tier?: unknown; settled?: unknown };
  const tier = c.tier === 'plus' || c.tier === 'pro' || c.tier === 'studio' || c.tier === 'free' ? c.tier : null;
  return { info: planInfo(data), checkout: { tier, settled: c.settled === true } };
}

/** What the server says a change will do (B4). Money is in the currency's minor unit; dates are ISO days. */
export interface PlanChangePreview {
  from_tier: SubscriptionTier;
  to_tier: SubscriptionTier;
  direction: 'up' | 'down' | 'cancel';
  when: 'now' | 'at_renewal';
  effective_on: string | null;
  due_now_cents: number;
  currency: string;
  then_cents: number;
  next_payment_on: string | null;
  proration_date: number | null;
  /** The subscription is set to cancel at the period end right now. */
  cancelling: boolean;
  /** Down and cancel only: this month's use against the smaller plan (counts and booleans, never money). */
  after: { reports_used: number; reports_included: number; reports_out: boolean; chat_out: boolean; refills_on: string } | null;
  billing_mode: 'test' | 'live' | 'off' | null;
}

const int = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : 0);
const day = (v: unknown): string | null => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null);
const tierOf = (v: unknown): SubscriptionTier | null => (v === 'free' || v === 'plus' || v === 'pro' || v === 'studio' ? v : null);

/** POST /change/preview {tier}: the price and dates of a move, before anything changes. */
export async function previewPlanChange(tier: SubscriptionTier, token: string): Promise<PlanChangePreview> {
  const res = await post('/change/preview', token, { tier });
  if (!res.ok) await planFailure(res, 'Couldn’t work out the price. Nothing changed.');
  const d = await res.json();
  const from = tierOf(d?.from_tier);
  const to = tierOf(d?.to_tier);
  const direction = d?.direction === 'up' || d?.direction === 'down' || d?.direction === 'cancel' ? d.direction : null;
  if (!from || !to || !direction) throw new PlanChangeError('Couldn’t work out the price. Nothing changed.', 'other', 200);
  const a = d?.after as Record<string, unknown> | null | undefined;
  return {
    from_tier: from, to_tier: to, direction,
    when: d?.when === 'now' ? 'now' : 'at_renewal',
    effective_on: day(d?.effective_on),
    due_now_cents: Math.max(0, int(d?.due_now_cents)),
    currency: typeof d?.currency === 'string' && /^[a-z]{3}$/i.test(d.currency) ? d.currency.toUpperCase() : 'USD',
    then_cents: Math.max(0, int(d?.then_cents)),
    next_payment_on: day(d?.next_payment_on),
    proration_date: typeof d?.proration_date === 'number' ? d.proration_date : null,
    cancelling: d?.cancelling === true,
    after: a && typeof a === 'object' && day(a.refills_on) ? {
      reports_used: int(a.reports_used), reports_included: int(a.reports_included),
      reports_out: a.reports_out === true, chat_out: a.chat_out === true, refills_on: day(a.refills_on)!,
    } : null,
    billing_mode: d?.billing_mode === 'test' || d?.billing_mode === 'live' || d?.billing_mode === 'off' ? d.billing_mode : null,
  };
}

/** POST /change {tier, proration_date}: up charges now (applied only once paid), down is scheduled, free cancels. */
export async function changePlan(tier: SubscriptionTier, prorationDate: number | null, token: string): Promise<SubscriptionInfo> {
  const res = await post('/change', token, prorationDate != null ? { tier, proration_date: prorationDate } : { tier });
  if (!res.ok) await planFailure(res, 'Nothing changed. Try again.');
  return planInfo(await res.json());
}

/** POST /cancel: cancel at the end of the billing month (idempotent). */
export async function cancelPlan(token: string): Promise<SubscriptionInfo> {
  const res = await post('/cancel', token);
  if (!res.ok) await planFailure(res, 'Nothing changed. Try again.');
  return planInfo(await res.json());
}

/** POST /resume: undo a pending cancel or a scheduled move down (idempotent). */
export async function resumePlan(token: string): Promise<SubscriptionInfo> {
  const res = await post('/resume', token);
  if (!res.ok) await planFailure(res, 'Nothing changed. Try again.');
  return planInfo(await res.json());
}

/** POST /sync: re-read the plan from Stripe (back from Card & invoices). Throttled server-side. */
export async function syncPlan(token: string): Promise<SubscriptionInfo> {
  const res = await post('/sync', token);
  if (!res.ok) await planFailure(res, 'Could not check your plan. Your subscription has not changed.');
  return planInfo(await res.json());
}
